import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query";

import { graphqlFetcher } from "~/api/fetchers";
import * as Types from "~/api/graphql-types";

export type GetResourcesQueryVariables = Types.Exact<{ [key: string]: never }>;

export type GetResourcesQuery = { __typename?: "Query" } & {
  workers?: Types.Maybe<
    { __typename?: "UserQueryPayload" } & {
      results: Array<
        { __typename?: "User" } & Pick<Types.User, "id" | "name"> & {
            workingHours?: Types.Maybe<
              Array<
                { __typename?: "WorkingHours" } & Pick<
                  Types.WorkingHours,
                  "id" | "weekday" | "working" | "startTime" | "endTime"
                >
              >
            >;
            services?: Types.Maybe<
              Array<
                { __typename?: "Service" } & Pick<
                  Types.Service,
                  "id" | "duration" | "name" | "requiredWorkers" | "requiredSpaces"
                >
              >
            >;
          }
      >;
    }
  >;
  spaces?: Types.Maybe<
    Array<
      { __typename?: "Location" } & Pick<Types.Location, "id" | "name"> & {
          services?: Types.Maybe<
            Array<
              { __typename?: "Service" } & Pick<
                Types.Service,
                "id" | "duration" | "name" | "requiredWorkers" | "requiredSpaces"
              >
            >
          >;
        }
    >
  >;
};

export type CreateBlockedTimeMutationVariables = Types.Exact<{
  input: Types.CreateResourceAdjustmentPeriodInput;
}>;

export type CreateBlockedTimeMutation = { __typename?: "Mutation" } & {
  createResourceAdjustmentPeriod: { __typename?: "ResourceAdjustmentPeriodPayload" } & {
    resourceAdjustmentPeriod?: Types.Maybe<
      { __typename?: "ResourceAdjustmentPeriod" } & Pick<
        Types.ResourceAdjustmentPeriod,
        "id" | "type" | "from" | "to" | "notes"
      > & {
          space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
          worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
          createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
        }
    >;
  };
};

export type UpdateBlockedTimeMutationVariables = Types.Exact<{
  input: Types.UpdateResourceAdjustmentPeriodInput;
}>;

export type UpdateBlockedTimeMutation = { __typename?: "Mutation" } & {
  updateResourceAdjustmentPeriod: { __typename?: "ResourceAdjustmentPeriodPayload" } & {
    resourceAdjustmentPeriod?: Types.Maybe<
      { __typename?: "ResourceAdjustmentPeriod" } & Pick<
        Types.ResourceAdjustmentPeriod,
        "id" | "type" | "from" | "to" | "notes"
      > & {
          space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
          worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
          createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
        }
    >;
  };
};

export type DeleteBlockedTimeMutationVariables = Types.Exact<{
  id: Types.Scalars["Long"]["input"];
}>;

export type DeleteBlockedTimeMutation = { __typename?: "Mutation" } & {
  deleteResourceAdjustmentPeriod?: Types.Maybe<
    { __typename?: "DeleteResourceAdjustmentPeriodPayload" } & Pick<
      Types.DeleteResourceAdjustmentPeriodPayload,
      "success"
    >
  >;
};

export type UpcomingAppointmentsForClientQueryVariables = Types.Exact<{
  clientId: Types.Scalars["Long"]["input"];
}>;

export type UpcomingAppointmentsForClientQuery = { __typename?: "Query" } & {
  upcomingAppointmentsForClient?: Types.Maybe<
    Array<
      { __typename?: "Appointment" } & Pick<
        Types.Appointment,
        | "id"
        | "from"
        | "duration"
        | "notes"
        | "name"
        | "cancelled"
        | "paid"
        | "confirmed"
        | "arrived"
        | "completed"
        | "accessLevel"
      > & {
          team?: Types.Maybe<{ __typename?: "Team" } & Pick<Types.Team, "id" | "name" | "key">>;
          service?: Types.Maybe<
            { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
          >;
          workers?: Types.Maybe<
            Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
          >;
          spaces?: Types.Maybe<
            Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
          >;
          client?: Types.Maybe<
            { __typename?: "Client" } & Pick<
              Types.Client,
              "id" | "name" | "firstName" | "lastName" | "email" | "level"
            >
          >;
        }
    >
  >;
};

export type AppointmentFragmentFragment = { __typename?: "Appointment" } & Pick<
  Types.Appointment,
  | "id"
  | "from"
  | "duration"
  | "notes"
  | "name"
  | "cancelled"
  | "paid"
  | "confirmed"
  | "arrived"
  | "completed"
> & {
    service?: Types.Maybe<
      { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
    >;
    workers?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
    >;
    spaces?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
    >;
    client?: Types.Maybe<
      { __typename?: "Client" } & Pick<
        Types.Client,
        "id" | "name" | "firstName" | "lastName" | "email" | "level"
      >
    >;
  };

export type CancelAppointmentMutationVariables = Types.Exact<{
  input: Types.CancelAppointmentInput;
}>;

export type CancelAppointmentMutation = { __typename?: "Mutation" } & {
  cancelAppointment: { __typename?: "AppointmentPayload" } & {
    appointment: { __typename?: "Appointment" } & Pick<
      Types.Appointment,
      | "id"
      | "from"
      | "duration"
      | "notes"
      | "name"
      | "cancelled"
      | "paid"
      | "confirmed"
      | "arrived"
      | "completed"
    > & {
        service?: Types.Maybe<
          { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
        >;
        workers?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
        >;
        spaces?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
        >;
        client?: Types.Maybe<
          { __typename?: "Client" } & Pick<
            Types.Client,
            "id" | "name" | "firstName" | "lastName" | "email" | "level"
          >
        >;
      };
  };
};

export type DeleteAppointmentMutationVariables = Types.Exact<{
  id: Types.Scalars["Long"]["input"];
}>;

export type DeleteAppointmentMutation = { __typename?: "Mutation" } & {
  deleteAppointment: { __typename?: "SuccessPayload" } & Pick<Types.SuccessPayload, "success">;
};

export type UpdateAppointmentWithWorkerAndLocationsMutationVariables = Types.Exact<{
  input: Types.UpdateAppointmentInput;
}>;

export type UpdateAppointmentWithWorkerAndLocationsMutation = { __typename?: "Mutation" } & {
  updateAppointment?: Types.Maybe<
    { __typename?: "UpdateAppointmentPayload" } & Pick<
      Types.UpdateAppointmentPayload,
      "success" | "message"
    > & {
        appointment?: Types.Maybe<
          { __typename?: "Appointment" } & Pick<
            Types.Appointment,
            | "id"
            | "from"
            | "duration"
            | "notes"
            | "name"
            | "cancelled"
            | "paid"
            | "confirmed"
            | "arrived"
            | "completed"
          > & {
              service?: Types.Maybe<
                { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
              >;
              workers?: Types.Maybe<
                Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
              >;
              spaces?: Types.Maybe<
                Array<
                  Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>
                >
              >;
              client?: Types.Maybe<
                { __typename?: "Client" } & Pick<
                  Types.Client,
                  "id" | "name" | "firstName" | "lastName" | "email" | "level"
                >
              >;
            }
        >;
        workerCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "WorkerCollision" } & Pick<Types.WorkerCollision, "id" | "name">
            >
          >
        >;
        locationCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "LocationCollision" } & Pick<Types.LocationCollision, "id" | "name">
            >
          >
        >;
      }
  >;
};

export type UpdateOrganisationTimezoneMutationVariables = Types.Exact<{
  organisation: Types.OrganisationInput;
}>;

export type UpdateOrganisationTimezoneMutation = { __typename?: "Mutation" } & {
  updateOrganisationTimezone?: Types.Maybe<
    { __typename?: "OrganisationPayload" } & {
      organisation: { __typename?: "Organisation" } & Pick<Types.Organisation, "timezone">;
    }
  >;
};

export type AppFragmentFragment = { __typename?: "Appointment" } & Pick<
  Types.Appointment,
  | "id"
  | "from"
  | "duration"
  | "notes"
  | "name"
  | "cancelled"
  | "paid"
  | "confirmed"
  | "arrived"
  | "completed"
> & {
    service?: Types.Maybe<
      { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
    >;
    workers?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
    >;
    spaces?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
    >;
    client?: Types.Maybe<
      { __typename?: "Client" } & Pick<Types.Client, "id" | "name" | "email" | "level">
    >;
    events?: Types.Maybe<
      Array<
        { __typename?: "AppointmentEvent" } & Pick<
          Types.AppointmentEvent,
          "id" | "eventType" | "eventDate" | "created"
        > & { createdBy?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">> }
      >
    >;
  };

export type ArriveAtAppointmentMutationVariables = Types.Exact<{
  input: Types.ArriveAtAppointmentInput;
}>;

export type ArriveAtAppointmentMutation = { __typename?: "Mutation" } & {
  arriveAtAppointment: { __typename?: "AppointmentPayload" } & {
    appointment: { __typename?: "Appointment" } & Pick<
      Types.Appointment,
      | "id"
      | "from"
      | "duration"
      | "notes"
      | "name"
      | "cancelled"
      | "paid"
      | "confirmed"
      | "arrived"
      | "completed"
    > & {
        service?: Types.Maybe<
          { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
        >;
        workers?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
        >;
        spaces?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
        >;
        client?: Types.Maybe<
          { __typename?: "Client" } & Pick<Types.Client, "id" | "name" | "email" | "level">
        >;
        events?: Types.Maybe<
          Array<
            { __typename?: "AppointmentEvent" } & Pick<
              Types.AppointmentEvent,
              "id" | "eventType" | "eventDate" | "created"
            > & {
                createdBy?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
              }
          >
        >;
      };
  };
};

export type CompleteAppointmentMutationVariables = Types.Exact<{
  input: Types.CompleteAppointmentInput;
}>;

export type CompleteAppointmentMutation = { __typename?: "Mutation" } & {
  completeAppointment: { __typename?: "AppointmentPayload" } & {
    appointment: { __typename?: "Appointment" } & Pick<
      Types.Appointment,
      | "id"
      | "from"
      | "duration"
      | "notes"
      | "name"
      | "cancelled"
      | "paid"
      | "confirmed"
      | "arrived"
      | "completed"
    > & {
        service?: Types.Maybe<
          { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
        >;
        workers?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
        >;
        spaces?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
        >;
        client?: Types.Maybe<
          { __typename?: "Client" } & Pick<Types.Client, "id" | "name" | "email" | "level">
        >;
        events?: Types.Maybe<
          Array<
            { __typename?: "AppointmentEvent" } & Pick<
              Types.AppointmentEvent,
              "id" | "eventType" | "eventDate" | "created"
            > & {
                createdBy?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
              }
          >
        >;
      };
  };
};

export type PayAppointmentMutationVariables = Types.Exact<{
  input: Types.PayAppointmentInput;
}>;

export type PayAppointmentMutation = { __typename?: "Mutation" } & {
  payAppointment: { __typename?: "AppointmentPayload" } & {
    appointment: { __typename?: "Appointment" } & Pick<
      Types.Appointment,
      | "id"
      | "from"
      | "duration"
      | "notes"
      | "name"
      | "cancelled"
      | "paid"
      | "confirmed"
      | "arrived"
      | "completed"
    > & {
        service?: Types.Maybe<
          { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
        >;
        workers?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
        >;
        spaces?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
        >;
        client?: Types.Maybe<
          { __typename?: "Client" } & Pick<Types.Client, "id" | "name" | "email" | "level">
        >;
        events?: Types.Maybe<
          Array<
            { __typename?: "AppointmentEvent" } & Pick<
              Types.AppointmentEvent,
              "id" | "eventType" | "eventDate" | "created"
            > & {
                createdBy?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
              }
          >
        >;
      };
  };
};

export type SchedulingStateQueryVariables = Types.Exact<{ [key: string]: never }>;

export type SchedulingStateQuery = { __typename?: "Query" } & {
  schedulingState: { __typename?: "SchedulingStatePayload" } & Pick<
    Types.SchedulingStatePayload,
    "hasTimezone" | "totalServices" | "totalBookableServices"
  > & {
      servicesMissingResources: Array<
        { __typename?: "Service" } & Pick<Types.Service, "id" | "name">
      >;
      servicesRequiringWorkingHours: Array<
        { __typename?: "Service" } & Pick<Types.Service, "id" | "name">
      >;
      workersRequiringWorkingHours: Array<
        { __typename?: "User" } & Pick<Types.User, "id" | "name">
      >;
    };
};

export type AppointmentQueryVariables = Types.Exact<{
  id?: Types.InputMaybe<Types.Scalars["Long"]["input"]>;
}>;

export type AppointmentQuery = { __typename?: "Query" } & {
  appointment: { __typename?: "Appointment" } & {
    events?: Types.Maybe<
      Array<
        { __typename?: "AppointmentEvent" } & Pick<
          Types.AppointmentEvent,
          "id" | "eventType" | "eventDate" | "created"
        > & { createdBy?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">> }
      >
    >;
  };
};

export type ClientPersonalDetailsQueryVariables = Types.Exact<{
  id?: Types.InputMaybe<Types.Scalars["Long"]["input"]>;
}>;

export type ClientPersonalDetailsQuery = { __typename?: "Query" } & {
  client?: Types.Maybe<
    { __typename?: "Client" } & Pick<
      Types.Client,
      "id" | "name" | "firstName" | "lastName" | "dateOfBirth" | "phone" | "email"
    >
  >;
};

export type AdjustmentPeriodsQueryVariables = Types.Exact<{
  input: Types.DateRangeInput;
}>;

export type AdjustmentPeriodsQuery = { __typename?: "Query" } & {
  resourceAdjustmentPeriodsBetween: Array<
    { __typename?: "ResourceAdjustmentPeriod" } & Pick<
      Types.ResourceAdjustmentPeriod,
      "id" | "type" | "from" | "to" | "notes"
    > & {
        space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
        worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
        createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
      }
  >;
};

export type UpdateResourceAdjustmentPeriodMutationVariables = Types.Exact<{
  input: Types.UpdateResourceAdjustmentPeriodInput;
}>;

export type UpdateResourceAdjustmentPeriodMutation = { __typename?: "Mutation" } & {
  updateResourceAdjustmentPeriod: { __typename?: "ResourceAdjustmentPeriodPayload" } & {
    resourceAdjustmentPeriod?: Types.Maybe<
      { __typename?: "ResourceAdjustmentPeriod" } & Pick<
        Types.ResourceAdjustmentPeriod,
        "id" | "type" | "from" | "to" | "notes"
      > & {
          space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
          worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
          createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
        }
    >;
  };
};

export type CreateClientMutationVariables = Types.Exact<{
  input: Types.CreateClientInput;
}>;

export type CreateClientMutation = { __typename?: "Mutation" } & {
  createClient?: Types.Maybe<
    { __typename?: "ClientPayload" } & {
      client?: Types.Maybe<
        { __typename?: "Client" } & Pick<
          Types.Client,
          | "id"
          | "name"
          | "firstName"
          | "lastName"
          | "dateOfBirth"
          | "email"
          | "phone"
          | "secondaryPhone"
          | "preferredName"
          | "medicalServiceId"
          | "clientId"
        > & {
            addresses?: Types.Maybe<
              Array<
                Types.Maybe<
                  { __typename?: "Address" } & Pick<
                    Types.Address,
                    "street1" | "street2" | "suburb" | "city" | "postcode" | "country"
                  >
                >
              >
            >;
          }
      >;
    }
  >;
};

export type ClientDuplicateQueryVariables = Types.Exact<{
  input: Types.CreateClientInput;
}>;

export type ClientDuplicateQuery = { __typename?: "Query" } & {
  duplicates: { __typename?: "ClientDuplicatesPayload" } & {
    duplicates: Array<
      { __typename?: "Client" } & Pick<
        Types.Client,
        "id" | "name" | "firstName" | "lastName" | "dateOfBirth" | "email"
      >
    >;
  };
};

export type CreateAdjustmentPeriodMutationVariables = Types.Exact<{
  input: Types.CreateResourceAdjustmentPeriodInput;
}>;

export type CreateAdjustmentPeriodMutation = { __typename?: "Mutation" } & {
  createResourceAdjustmentPeriod: { __typename?: "ResourceAdjustmentPeriodPayload" } & {
    resourceAdjustmentPeriod?: Types.Maybe<
      { __typename?: "ResourceAdjustmentPeriod" } & Pick<Types.ResourceAdjustmentPeriod, "id">
    >;
    conflictingPeriods?: Types.Maybe<
      Array<
        { __typename?: "ResourceAdjustmentPeriod" } & Pick<
          Types.ResourceAdjustmentPeriod,
          "id" | "type" | "from" | "to" | "notes"
        > & {
            space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
            worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
            createdBy: { __typename?: "User" } & Pick<Types.User, "id">;
          }
      >
    >;
  };
};

export type UpdateAdjustmentPeriodMutationVariables = Types.Exact<{
  input: Types.UpdateResourceAdjustmentPeriodInput;
}>;

export type UpdateAdjustmentPeriodMutation = { __typename?: "Mutation" } & {
  updateResourceAdjustmentPeriod: { __typename?: "ResourceAdjustmentPeriodPayload" } & {
    resourceAdjustmentPeriod?: Types.Maybe<
      { __typename?: "ResourceAdjustmentPeriod" } & Pick<Types.ResourceAdjustmentPeriod, "id">
    >;
    conflictingPeriods?: Types.Maybe<
      Array<
        { __typename?: "ResourceAdjustmentPeriod" } & Pick<
          Types.ResourceAdjustmentPeriod,
          "id" | "type" | "from" | "to" | "notes"
        > & {
            space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
            worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
            createdBy: { __typename?: "User" } & Pick<Types.User, "id">;
          }
      >
    >;
  };
};

export type DeleteResourceAdjustmentPeriodMutationVariables = Types.Exact<{
  id: Types.Scalars["Long"]["input"];
}>;

export type DeleteResourceAdjustmentPeriodMutation = { __typename?: "Mutation" } & {
  deleteResourceAdjustmentPeriod?: Types.Maybe<
    { __typename?: "DeleteResourceAdjustmentPeriodPayload" } & Pick<
      Types.DeleteResourceAdjustmentPeriodPayload,
      "success"
    >
  >;
};

export type TimeslotsQueryVariables = Types.Exact<{
  input: Types.TimeslotInput;
}>;

export type TimeslotsQuery = { __typename?: "Query" } & {
  timeslots: Array<
    { __typename?: "Timeslot" } & Pick<
      Types.Timeslot,
      "from" | "workerIds" | "spaceIds" | "clientId" | "serviceId"
    >
  >;
};

export type AppointmentFragment = { __typename?: "Appointment" } & Pick<
  Types.Appointment,
  | "id"
  | "from"
  | "duration"
  | "notes"
  | "name"
  | "cancelled"
  | "paid"
  | "confirmed"
  | "arrived"
  | "completed"
  | "accessLevel"
> & {
    team?: Types.Maybe<{ __typename?: "Team" } & Pick<Types.Team, "id" | "name" | "key">>;
    service?: Types.Maybe<
      { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
    >;
    workers?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
    >;
    spaces?: Types.Maybe<
      Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
    >;
    client?: Types.Maybe<
      { __typename?: "Client" } & Pick<
        Types.Client,
        "id" | "name" | "firstName" | "lastName" | "email" | "level"
      >
    >;
  };

export type WorkersAndTheirWorkingHoursFragment = { __typename?: "UserQueryPayload" } & {
  results: Array<
    { __typename?: "User" } & Pick<Types.User, "id" | "name"> & {
        workingHours?: Types.Maybe<
          Array<
            { __typename?: "WorkingHours" } & Pick<
              Types.WorkingHours,
              "id" | "weekday" | "working" | "startTime" | "endTime"
            >
          >
        >;
      }
  >;
};

export type ResourceAdjustmentPeriodFragment = { __typename?: "ResourceAdjustmentPeriod" } & Pick<
  Types.ResourceAdjustmentPeriod,
  "id" | "type" | "from" | "to" | "notes"
> & {
    space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
    worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
    createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
  };

export type GetCalendarItemsQueryVariables = Types.Exact<{
  input: Types.DateRangeInput;
}>;

export type GetCalendarItemsQuery = { __typename?: "Query" } & {
  appointmentsForPeriod: Array<
    { __typename?: "Appointment" } & Pick<
      Types.Appointment,
      | "id"
      | "from"
      | "duration"
      | "notes"
      | "name"
      | "cancelled"
      | "paid"
      | "confirmed"
      | "arrived"
      | "completed"
      | "accessLevel"
    > & {
        team?: Types.Maybe<{ __typename?: "Team" } & Pick<Types.Team, "id" | "name" | "key">>;
        service?: Types.Maybe<
          { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
        >;
        workers?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
        >;
        spaces?: Types.Maybe<
          Array<Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>
        >;
        client?: Types.Maybe<
          { __typename?: "Client" } & Pick<
            Types.Client,
            "id" | "name" | "firstName" | "lastName" | "email" | "level"
          >
        >;
      }
  >;
  resourceAdjustmentPeriodsBetween: Array<
    { __typename?: "ResourceAdjustmentPeriod" } & Pick<
      Types.ResourceAdjustmentPeriod,
      "id" | "type" | "from" | "to" | "notes"
    > & {
        space?: Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>;
        worker?: Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>;
        createdBy: { __typename?: "User" } & Pick<Types.User, "id" | "name">;
      }
  >;
  workers?: Types.Maybe<
    { __typename?: "UserQueryPayload" } & {
      results: Array<
        { __typename?: "User" } & Pick<Types.User, "id" | "name"> & {
            workingHours?: Types.Maybe<
              Array<
                { __typename?: "WorkingHours" } & Pick<
                  Types.WorkingHours,
                  "id" | "weekday" | "working" | "startTime" | "endTime"
                >
              >
            >;
          }
      >;
    }
  >;
  spaces?: Types.Maybe<Array<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>>;
};

export type CreateAppointmentMutationVariables = Types.Exact<{
  input: Types.CreateAppointmentInput;
}>;

export type CreateAppointmentMutation = { __typename?: "Mutation" } & {
  createAppointment?: Types.Maybe<
    { __typename?: "CreateAppointmentPayload" } & Pick<
      Types.CreateAppointmentPayload,
      "success" | "message"
    > & {
        appointment?: Types.Maybe<
          { __typename?: "Appointment" } & Pick<
            Types.Appointment,
            | "id"
            | "from"
            | "duration"
            | "notes"
            | "name"
            | "cancelled"
            | "paid"
            | "confirmed"
            | "arrived"
            | "completed"
            | "accessLevel"
          > & {
              team?: Types.Maybe<{ __typename?: "Team" } & Pick<Types.Team, "id" | "name" | "key">>;
              service?: Types.Maybe<
                { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
              >;
              workers?: Types.Maybe<
                Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
              >;
              spaces?: Types.Maybe<
                Array<
                  Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>
                >
              >;
              client?: Types.Maybe<
                { __typename?: "Client" } & Pick<
                  Types.Client,
                  "id" | "name" | "firstName" | "lastName" | "email" | "level"
                >
              >;
            }
        >;
        workerCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "WorkerCollision" } & Pick<Types.WorkerCollision, "id" | "name">
            >
          >
        >;
        locationCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "LocationCollision" } & Pick<Types.LocationCollision, "id" | "name">
            >
          >
        >;
      }
  >;
};

export type UpdateAppointmentMutationVariables = Types.Exact<{
  input: Types.UpdateAppointmentInput;
}>;

export type UpdateAppointmentMutation = { __typename?: "Mutation" } & {
  updateAppointment?: Types.Maybe<
    { __typename?: "UpdateAppointmentPayload" } & Pick<
      Types.UpdateAppointmentPayload,
      "success" | "message"
    > & {
        appointment?: Types.Maybe<
          { __typename?: "Appointment" } & Pick<
            Types.Appointment,
            | "id"
            | "from"
            | "duration"
            | "notes"
            | "name"
            | "cancelled"
            | "paid"
            | "confirmed"
            | "arrived"
            | "completed"
            | "accessLevel"
          > & {
              team?: Types.Maybe<{ __typename?: "Team" } & Pick<Types.Team, "id" | "name" | "key">>;
              service?: Types.Maybe<
                { __typename?: "Service" } & Pick<Types.Service, "id" | "name" | "color">
              >;
              workers?: Types.Maybe<
                Array<Types.Maybe<{ __typename?: "User" } & Pick<Types.User, "id" | "name">>>
              >;
              spaces?: Types.Maybe<
                Array<
                  Types.Maybe<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>
                >
              >;
              client?: Types.Maybe<
                { __typename?: "Client" } & Pick<
                  Types.Client,
                  "id" | "name" | "firstName" | "lastName" | "email" | "level"
                >
              >;
            }
        >;
        workerCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "WorkerCollision" } & Pick<Types.WorkerCollision, "id" | "name">
            >
          >
        >;
        locationCollisions?: Types.Maybe<
          Array<
            Types.Maybe<
              { __typename?: "LocationCollision" } & Pick<Types.LocationCollision, "id" | "name">
            >
          >
        >;
      }
  >;
};

export type UpdateClientMutationVariables = Types.Exact<{
  input: Types.UpdateClientInput;
}>;

export type UpdateClientMutation = { __typename?: "Mutation" } & {
  updateClient?: Types.Maybe<
    { __typename?: "ClientPayload" } & {
      client?: Types.Maybe<
        { __typename?: "Client" } & Pick<
          Types.Client,
          "id" | "name" | "firstName" | "lastName" | "dateOfBirth" | "phone" | "email"
        >
      >;
    }
  >;
};

export type GetServicesQueryVariables = Types.Exact<{ [key: string]: never }>;

export type GetServicesQuery = { __typename?: "Query" } & {
  services?: Types.Maybe<
    Array<
      { __typename?: "Service" } & Pick<
        Types.Service,
        | "id"
        | "name"
        | "description"
        | "duration"
        | "price"
        | "color"
        | "requiredWorkers"
        | "requiredSpaces"
      > & {
          workers?: Types.Maybe<
            Array<{ __typename?: "User" } & Pick<Types.User, "id" | "name" | "email">>
          >;
          spaces?: Types.Maybe<
            Array<{ __typename?: "Location" } & Pick<Types.Location, "id" | "name">>
          >;
        }
    >
  >;
};

export const AppointmentFragmentFragmentDoc = `
    fragment AppointmentFragment on Appointment {
  id
  from
  duration
  notes
  name
  cancelled
  paid
  confirmed
  arrived
  completed
  service {
    id
    name
    color
  }
  workers {
    id
    name
  }
  spaces {
    id
    name
  }
  client {
    id
    name
    firstName
    lastName
    email
    level
  }
}
    `;
export const AppFragmentFragmentDoc = `
    fragment AppFragment on Appointment {
  id
  from
  duration
  notes
  name
  cancelled
  paid
  confirmed
  arrived
  completed
  service {
    id
    name
    color
  }
  workers {
    id
    name
  }
  spaces {
    id
    name
  }
  client {
    id
    name
    email
    level
  }
  events {
    id
    eventType
    eventDate
    createdBy {
      id
      name
    }
    created
  }
}
    `;
export const AppointmentFragmentDoc = `
    fragment Appointment on Appointment {
  id
  from
  duration
  notes
  name
  team {
    id
    name
    key
  }
  cancelled
  paid
  confirmed
  arrived
  completed
  service {
    id
    name
    color
  }
  workers {
    id
    name
  }
  spaces {
    id
    name
  }
  client {
    id
    name
    firstName
    lastName
    email
    level
  }
  accessLevel
}
    `;
export const WorkersAndTheirWorkingHoursFragmentDoc = `
    fragment WorkersAndTheirWorkingHours on UserQueryPayload {
  results {
    id
    name
    workingHours {
      id
      weekday
      working
      startTime
      endTime
    }
  }
}
    `;
export const ResourceAdjustmentPeriodFragmentDoc = `
    fragment ResourceAdjustmentPeriod on ResourceAdjustmentPeriod {
  id
  space: location {
    id
    name
  }
  worker {
    id
    name
  }
  type
  from
  to
  notes
  createdBy {
    id
    name
  }
}
    `;
export const GetResourcesDocument = `
    query getResources {
  workers {
    results {
      id
      name
      workingHours {
        id
        weekday
        working
        startTime
        endTime
      }
      services {
        id
        duration
        name
        requiredWorkers
        requiredSpaces
      }
    }
  }
  spaces: locations {
    id
    name
    services {
      id
      duration
      name
      requiredWorkers
      requiredSpaces
    }
  }
}
    `;

export const useGetResourcesQuery = <TData = GetResourcesQuery, TError = unknown>(
  variables?: GetResourcesQueryVariables,
  options?: Omit<UseQueryOptions<GetResourcesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<GetResourcesQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<GetResourcesQuery, TError, TData>({
    queryKey: variables === undefined ? ["getResources"] : ["getResources", variables],
    queryFn: graphqlFetcher<GetResourcesQuery, GetResourcesQueryVariables>(
      GetResourcesDocument,
      variables
    ),
    ...options,
  });
};

useGetResourcesQuery.getKey = (variables?: GetResourcesQueryVariables) =>
  variables === undefined ? ["getResources"] : ["getResources", variables];

export const CreateBlockedTimeDocument = `
    mutation createBlockedTime($input: CreateResourceAdjustmentPeriodInput!) {
  createResourceAdjustmentPeriod(input: $input) {
    resourceAdjustmentPeriod {
      id
      space: location {
        id
        name
      }
      worker {
        id
        name
      }
      type
      from
      to
      notes
      createdBy {
        id
        name
      }
    }
  }
}
    `;

export const useCreateBlockedTimeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CreateBlockedTimeMutation,
    TError,
    CreateBlockedTimeMutationVariables,
    TContext
  >
) => {
  return useMutation<
    CreateBlockedTimeMutation,
    TError,
    CreateBlockedTimeMutationVariables,
    TContext
  >({
    mutationKey: ["createBlockedTime"],
    mutationFn: (variables?: CreateBlockedTimeMutationVariables) =>
      graphqlFetcher<CreateBlockedTimeMutation, CreateBlockedTimeMutationVariables>(
        CreateBlockedTimeDocument,
        variables
      )(),
    ...options,
  });
};

export const UpdateBlockedTimeDocument = `
    mutation updateBlockedTime($input: UpdateResourceAdjustmentPeriodInput!) {
  updateResourceAdjustmentPeriod(input: $input) {
    resourceAdjustmentPeriod {
      id
      space: location {
        id
        name
      }
      worker {
        id
        name
      }
      type
      from
      to
      notes
      createdBy {
        id
        name
      }
    }
  }
}
    `;

export const useUpdateBlockedTimeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateBlockedTimeMutation,
    TError,
    UpdateBlockedTimeMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateBlockedTimeMutation,
    TError,
    UpdateBlockedTimeMutationVariables,
    TContext
  >({
    mutationKey: ["updateBlockedTime"],
    mutationFn: (variables?: UpdateBlockedTimeMutationVariables) =>
      graphqlFetcher<UpdateBlockedTimeMutation, UpdateBlockedTimeMutationVariables>(
        UpdateBlockedTimeDocument,
        variables
      )(),
    ...options,
  });
};

export const DeleteBlockedTimeDocument = `
    mutation deleteBlockedTime($id: Long!) {
  deleteResourceAdjustmentPeriod(id: $id) {
    success
  }
}
    `;

export const useDeleteBlockedTimeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    DeleteBlockedTimeMutation,
    TError,
    DeleteBlockedTimeMutationVariables,
    TContext
  >
) => {
  return useMutation<
    DeleteBlockedTimeMutation,
    TError,
    DeleteBlockedTimeMutationVariables,
    TContext
  >({
    mutationKey: ["deleteBlockedTime"],
    mutationFn: (variables?: DeleteBlockedTimeMutationVariables) =>
      graphqlFetcher<DeleteBlockedTimeMutation, DeleteBlockedTimeMutationVariables>(
        DeleteBlockedTimeDocument,
        variables
      )(),
    ...options,
  });
};

export const UpcomingAppointmentsForClientDocument = `
    query upcomingAppointmentsForClient($clientId: Long!) {
  upcomingAppointmentsForClient(clientId: $clientId) {
    ...Appointment
  }
}
    ${AppointmentFragmentDoc}`;

export const useUpcomingAppointmentsForClientQuery = <
  TData = UpcomingAppointmentsForClientQuery,
  TError = unknown,
>(
  variables: UpcomingAppointmentsForClientQueryVariables,
  options?: Omit<UseQueryOptions<UpcomingAppointmentsForClientQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<UpcomingAppointmentsForClientQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<UpcomingAppointmentsForClientQuery, TError, TData>({
    queryKey: ["upcomingAppointmentsForClient", variables],
    queryFn: graphqlFetcher<
      UpcomingAppointmentsForClientQuery,
      UpcomingAppointmentsForClientQueryVariables
    >(UpcomingAppointmentsForClientDocument, variables),
    ...options,
  });
};

useUpcomingAppointmentsForClientQuery.getKey = (
  variables: UpcomingAppointmentsForClientQueryVariables
) => ["upcomingAppointmentsForClient", variables];

export const CancelAppointmentDocument = `
    mutation cancelAppointment($input: CancelAppointmentInput!) {
  cancelAppointment(input: $input) {
    appointment {
      ...AppointmentFragment
    }
  }
}
    ${AppointmentFragmentFragmentDoc}`;

export const useCancelAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CancelAppointmentMutation,
    TError,
    CancelAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    CancelAppointmentMutation,
    TError,
    CancelAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["cancelAppointment"],
    mutationFn: (variables?: CancelAppointmentMutationVariables) =>
      graphqlFetcher<CancelAppointmentMutation, CancelAppointmentMutationVariables>(
        CancelAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const DeleteAppointmentDocument = `
    mutation deleteAppointment($id: Long!) {
  deleteAppointment(id: $id) {
    success
  }
}
    `;

export const useDeleteAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    DeleteAppointmentMutation,
    TError,
    DeleteAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    DeleteAppointmentMutation,
    TError,
    DeleteAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["deleteAppointment"],
    mutationFn: (variables?: DeleteAppointmentMutationVariables) =>
      graphqlFetcher<DeleteAppointmentMutation, DeleteAppointmentMutationVariables>(
        DeleteAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const UpdateAppointmentWithWorkerAndLocationsDocument = `
    mutation updateAppointmentWithWorkerAndLocations($input: UpdateAppointmentInput!) {
  updateAppointment(input: $input) {
    appointment {
      ...AppointmentFragment
    }
    workerCollisions {
      id
      name
    }
    locationCollisions {
      id
      name
    }
    success
    message
  }
}
    ${AppointmentFragmentFragmentDoc}`;

export const useUpdateAppointmentWithWorkerAndLocationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    UpdateAppointmentWithWorkerAndLocationsMutation,
    TError,
    UpdateAppointmentWithWorkerAndLocationsMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateAppointmentWithWorkerAndLocationsMutation,
    TError,
    UpdateAppointmentWithWorkerAndLocationsMutationVariables,
    TContext
  >({
    mutationKey: ["updateAppointmentWithWorkerAndLocations"],
    mutationFn: (variables?: UpdateAppointmentWithWorkerAndLocationsMutationVariables) =>
      graphqlFetcher<
        UpdateAppointmentWithWorkerAndLocationsMutation,
        UpdateAppointmentWithWorkerAndLocationsMutationVariables
      >(UpdateAppointmentWithWorkerAndLocationsDocument, variables)(),
    ...options,
  });
};

export const UpdateOrganisationTimezoneDocument = `
    mutation updateOrganisationTimezone($organisation: OrganisationInput!) {
  updateOrganisationTimezone(input: $organisation) {
    organisation {
      timezone
    }
  }
}
    `;

export const useUpdateOrganisationTimezoneMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateOrganisationTimezoneMutation,
    TError,
    UpdateOrganisationTimezoneMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateOrganisationTimezoneMutation,
    TError,
    UpdateOrganisationTimezoneMutationVariables,
    TContext
  >({
    mutationKey: ["updateOrganisationTimezone"],
    mutationFn: (variables?: UpdateOrganisationTimezoneMutationVariables) =>
      graphqlFetcher<
        UpdateOrganisationTimezoneMutation,
        UpdateOrganisationTimezoneMutationVariables
      >(UpdateOrganisationTimezoneDocument, variables)(),
    ...options,
  });
};

export const ArriveAtAppointmentDocument = `
    mutation arriveAtAppointment($input: ArriveAtAppointmentInput!) {
  arriveAtAppointment(input: $input) {
    appointment {
      ...AppFragment
    }
  }
}
    ${AppFragmentFragmentDoc}`;

export const useArriveAtAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    ArriveAtAppointmentMutation,
    TError,
    ArriveAtAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    ArriveAtAppointmentMutation,
    TError,
    ArriveAtAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["arriveAtAppointment"],
    mutationFn: (variables?: ArriveAtAppointmentMutationVariables) =>
      graphqlFetcher<ArriveAtAppointmentMutation, ArriveAtAppointmentMutationVariables>(
        ArriveAtAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const CompleteAppointmentDocument = `
    mutation completeAppointment($input: CompleteAppointmentInput!) {
  completeAppointment(input: $input) {
    appointment {
      ...AppFragment
    }
  }
}
    ${AppFragmentFragmentDoc}`;

export const useCompleteAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CompleteAppointmentMutation,
    TError,
    CompleteAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    CompleteAppointmentMutation,
    TError,
    CompleteAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["completeAppointment"],
    mutationFn: (variables?: CompleteAppointmentMutationVariables) =>
      graphqlFetcher<CompleteAppointmentMutation, CompleteAppointmentMutationVariables>(
        CompleteAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const PayAppointmentDocument = `
    mutation payAppointment($input: PayAppointmentInput!) {
  payAppointment(input: $input) {
    appointment {
      ...AppFragment
    }
  }
}
    ${AppFragmentFragmentDoc}`;

export const usePayAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    PayAppointmentMutation,
    TError,
    PayAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<PayAppointmentMutation, TError, PayAppointmentMutationVariables, TContext>({
    mutationKey: ["payAppointment"],
    mutationFn: (variables?: PayAppointmentMutationVariables) =>
      graphqlFetcher<PayAppointmentMutation, PayAppointmentMutationVariables>(
        PayAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const SchedulingStateDocument = `
    query schedulingState {
  schedulingState {
    hasTimezone
    totalServices
    totalBookableServices
    servicesMissingResources {
      id
      name
    }
    servicesRequiringWorkingHours {
      id
      name
    }
    workersRequiringWorkingHours {
      id
      name
    }
  }
}
    `;

export const useSchedulingStateQuery = <TData = SchedulingStateQuery, TError = unknown>(
  variables?: SchedulingStateQueryVariables,
  options?: Omit<UseQueryOptions<SchedulingStateQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<SchedulingStateQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<SchedulingStateQuery, TError, TData>({
    queryKey: variables === undefined ? ["schedulingState"] : ["schedulingState", variables],
    queryFn: graphqlFetcher<SchedulingStateQuery, SchedulingStateQueryVariables>(
      SchedulingStateDocument,
      variables
    ),
    ...options,
  });
};

useSchedulingStateQuery.getKey = (variables?: SchedulingStateQueryVariables) =>
  variables === undefined ? ["schedulingState"] : ["schedulingState", variables];

export const AppointmentDocument = `
    query appointment($id: Long) {
  appointment(id: $id) {
    events {
      id
      eventType
      eventDate
      createdBy {
        id
        name
      }
      created
    }
  }
}
    `;

export const useAppointmentQuery = <TData = AppointmentQuery, TError = unknown>(
  variables?: AppointmentQueryVariables,
  options?: Omit<UseQueryOptions<AppointmentQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<AppointmentQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<AppointmentQuery, TError, TData>({
    queryKey: variables === undefined ? ["appointment"] : ["appointment", variables],
    queryFn: graphqlFetcher<AppointmentQuery, AppointmentQueryVariables>(
      AppointmentDocument,
      variables
    ),
    ...options,
  });
};

useAppointmentQuery.getKey = (variables?: AppointmentQueryVariables) =>
  variables === undefined ? ["appointment"] : ["appointment", variables];

export const ClientPersonalDetailsDocument = `
    query clientPersonalDetails($id: Long) {
  client(id: $id) {
    id
    name
    firstName
    lastName
    dateOfBirth
    phone
    email
  }
}
    `;

export const useClientPersonalDetailsQuery = <TData = ClientPersonalDetailsQuery, TError = unknown>(
  variables?: ClientPersonalDetailsQueryVariables,
  options?: Omit<UseQueryOptions<ClientPersonalDetailsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<ClientPersonalDetailsQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<ClientPersonalDetailsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["clientPersonalDetails"] : ["clientPersonalDetails", variables],
    queryFn: graphqlFetcher<ClientPersonalDetailsQuery, ClientPersonalDetailsQueryVariables>(
      ClientPersonalDetailsDocument,
      variables
    ),
    ...options,
  });
};

useClientPersonalDetailsQuery.getKey = (variables?: ClientPersonalDetailsQueryVariables) =>
  variables === undefined ? ["clientPersonalDetails"] : ["clientPersonalDetails", variables];

export const AdjustmentPeriodsDocument = `
    query adjustmentPeriods($input: DateRangeInput!) {
  resourceAdjustmentPeriodsBetween(input: $input) {
    ...ResourceAdjustmentPeriod
  }
}
    ${ResourceAdjustmentPeriodFragmentDoc}`;

export const useAdjustmentPeriodsQuery = <TData = AdjustmentPeriodsQuery, TError = unknown>(
  variables: AdjustmentPeriodsQueryVariables,
  options?: Omit<UseQueryOptions<AdjustmentPeriodsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<AdjustmentPeriodsQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<AdjustmentPeriodsQuery, TError, TData>({
    queryKey: ["adjustmentPeriods", variables],
    queryFn: graphqlFetcher<AdjustmentPeriodsQuery, AdjustmentPeriodsQueryVariables>(
      AdjustmentPeriodsDocument,
      variables
    ),
    ...options,
  });
};

useAdjustmentPeriodsQuery.getKey = (variables: AdjustmentPeriodsQueryVariables) => [
  "adjustmentPeriods",
  variables,
];

export const UpdateResourceAdjustmentPeriodDocument = `
    mutation updateResourceAdjustmentPeriod($input: UpdateResourceAdjustmentPeriodInput!) {
  updateResourceAdjustmentPeriod(input: $input) {
    resourceAdjustmentPeriod {
      id
      space: location {
        id
        name
      }
      worker {
        id
        name
      }
      type
      from
      to
      notes
      createdBy {
        id
        name
      }
    }
  }
}
    `;

export const useUpdateResourceAdjustmentPeriodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateResourceAdjustmentPeriodMutation,
    TError,
    UpdateResourceAdjustmentPeriodMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateResourceAdjustmentPeriodMutation,
    TError,
    UpdateResourceAdjustmentPeriodMutationVariables,
    TContext
  >({
    mutationKey: ["updateResourceAdjustmentPeriod"],
    mutationFn: (variables?: UpdateResourceAdjustmentPeriodMutationVariables) =>
      graphqlFetcher<
        UpdateResourceAdjustmentPeriodMutation,
        UpdateResourceAdjustmentPeriodMutationVariables
      >(UpdateResourceAdjustmentPeriodDocument, variables)(),
    ...options,
  });
};

export const CreateClientDocument = `
    mutation createClient($input: CreateClientInput!) {
  createClient(input: $input) {
    client {
      id
      name
      firstName
      lastName
      dateOfBirth
      email
      phone
      secondaryPhone
      preferredName
      medicalServiceId
      clientId
      addresses {
        street1
        street2
        suburb
        city
        postcode
        country
      }
    }
  }
}
    `;

export const useCreateClientMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CreateClientMutation,
    TError,
    CreateClientMutationVariables,
    TContext
  >
) => {
  return useMutation<CreateClientMutation, TError, CreateClientMutationVariables, TContext>({
    mutationKey: ["createClient"],
    mutationFn: (variables?: CreateClientMutationVariables) =>
      graphqlFetcher<CreateClientMutation, CreateClientMutationVariables>(
        CreateClientDocument,
        variables
      )(),
    ...options,
  });
};

export const ClientDuplicateDocument = `
    query clientDuplicate($input: CreateClientInput!) {
  duplicates(input: $input) {
    duplicates {
      id
      name
      firstName
      lastName
      dateOfBirth
      email
    }
  }
}
    `;

export const useClientDuplicateQuery = <TData = ClientDuplicateQuery, TError = unknown>(
  variables: ClientDuplicateQueryVariables,
  options?: Omit<UseQueryOptions<ClientDuplicateQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<ClientDuplicateQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<ClientDuplicateQuery, TError, TData>({
    queryKey: ["clientDuplicate", variables],
    queryFn: graphqlFetcher<ClientDuplicateQuery, ClientDuplicateQueryVariables>(
      ClientDuplicateDocument,
      variables
    ),
    ...options,
  });
};

useClientDuplicateQuery.getKey = (variables: ClientDuplicateQueryVariables) => [
  "clientDuplicate",
  variables,
];

export const CreateAdjustmentPeriodDocument = `
    mutation createAdjustmentPeriod($input: CreateResourceAdjustmentPeriodInput!) {
  createResourceAdjustmentPeriod(input: $input) {
    resourceAdjustmentPeriod {
      id
    }
    conflictingPeriods {
      id
      space: location {
        id
        name
      }
      worker {
        id
        name
      }
      type
      from
      to
      notes
      createdBy {
        id
      }
    }
  }
}
    `;

export const useCreateAdjustmentPeriodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CreateAdjustmentPeriodMutation,
    TError,
    CreateAdjustmentPeriodMutationVariables,
    TContext
  >
) => {
  return useMutation<
    CreateAdjustmentPeriodMutation,
    TError,
    CreateAdjustmentPeriodMutationVariables,
    TContext
  >({
    mutationKey: ["createAdjustmentPeriod"],
    mutationFn: (variables?: CreateAdjustmentPeriodMutationVariables) =>
      graphqlFetcher<CreateAdjustmentPeriodMutation, CreateAdjustmentPeriodMutationVariables>(
        CreateAdjustmentPeriodDocument,
        variables
      )(),
    ...options,
  });
};

export const UpdateAdjustmentPeriodDocument = `
    mutation updateAdjustmentPeriod($input: UpdateResourceAdjustmentPeriodInput!) {
  updateResourceAdjustmentPeriod(input: $input) {
    resourceAdjustmentPeriod {
      id
    }
    conflictingPeriods {
      id
      space: location {
        id
        name
      }
      worker {
        id
        name
      }
      type
      from
      to
      notes
      createdBy {
        id
      }
    }
  }
}
    `;

export const useUpdateAdjustmentPeriodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateAdjustmentPeriodMutation,
    TError,
    UpdateAdjustmentPeriodMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateAdjustmentPeriodMutation,
    TError,
    UpdateAdjustmentPeriodMutationVariables,
    TContext
  >({
    mutationKey: ["updateAdjustmentPeriod"],
    mutationFn: (variables?: UpdateAdjustmentPeriodMutationVariables) =>
      graphqlFetcher<UpdateAdjustmentPeriodMutation, UpdateAdjustmentPeriodMutationVariables>(
        UpdateAdjustmentPeriodDocument,
        variables
      )(),
    ...options,
  });
};

export const DeleteResourceAdjustmentPeriodDocument = `
    mutation deleteResourceAdjustmentPeriod($id: Long!) {
  deleteResourceAdjustmentPeriod(id: $id) {
    success
  }
}
    `;

export const useDeleteResourceAdjustmentPeriodMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    DeleteResourceAdjustmentPeriodMutation,
    TError,
    DeleteResourceAdjustmentPeriodMutationVariables,
    TContext
  >
) => {
  return useMutation<
    DeleteResourceAdjustmentPeriodMutation,
    TError,
    DeleteResourceAdjustmentPeriodMutationVariables,
    TContext
  >({
    mutationKey: ["deleteResourceAdjustmentPeriod"],
    mutationFn: (variables?: DeleteResourceAdjustmentPeriodMutationVariables) =>
      graphqlFetcher<
        DeleteResourceAdjustmentPeriodMutation,
        DeleteResourceAdjustmentPeriodMutationVariables
      >(DeleteResourceAdjustmentPeriodDocument, variables)(),
    ...options,
  });
};

export const TimeslotsDocument = `
    query timeslots($input: TimeslotInput!) {
  timeslots(input: $input) {
    from
    workerIds
    spaceIds
    clientId
    serviceId
  }
}
    `;

export const useTimeslotsQuery = <TData = TimeslotsQuery, TError = unknown>(
  variables: TimeslotsQueryVariables,
  options?: Omit<UseQueryOptions<TimeslotsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<TimeslotsQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<TimeslotsQuery, TError, TData>({
    queryKey: ["timeslots", variables],
    queryFn: graphqlFetcher<TimeslotsQuery, TimeslotsQueryVariables>(TimeslotsDocument, variables),
    ...options,
  });
};

useTimeslotsQuery.getKey = (variables: TimeslotsQueryVariables) => ["timeslots", variables];

export const GetCalendarItemsDocument = `
    query getCalendarItems($input: DateRangeInput!) {
  appointmentsForPeriod(input: $input) {
    ...Appointment
  }
  resourceAdjustmentPeriodsBetween(input: $input) {
    ...ResourceAdjustmentPeriod
  }
  workers {
    ...WorkersAndTheirWorkingHours
  }
  spaces: locations {
    id
    name
  }
}
    ${AppointmentFragmentDoc}
${ResourceAdjustmentPeriodFragmentDoc}
${WorkersAndTheirWorkingHoursFragmentDoc}`;

export const useGetCalendarItemsQuery = <TData = GetCalendarItemsQuery, TError = unknown>(
  variables: GetCalendarItemsQueryVariables,
  options?: Omit<UseQueryOptions<GetCalendarItemsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<GetCalendarItemsQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<GetCalendarItemsQuery, TError, TData>({
    queryKey: ["getCalendarItems", variables],
    queryFn: graphqlFetcher<GetCalendarItemsQuery, GetCalendarItemsQueryVariables>(
      GetCalendarItemsDocument,
      variables
    ),
    ...options,
  });
};

useGetCalendarItemsQuery.getKey = (variables: GetCalendarItemsQueryVariables) => [
  "getCalendarItems",
  variables,
];

export const CreateAppointmentDocument = `
    mutation createAppointment($input: CreateAppointmentInput!) {
  createAppointment(input: $input) {
    appointment {
      ...Appointment
    }
    workerCollisions {
      id
      name
    }
    locationCollisions {
      id
      name
    }
    success
    message
  }
}
    ${AppointmentFragmentDoc}`;

export const useCreateAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    CreateAppointmentMutation,
    TError,
    CreateAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    CreateAppointmentMutation,
    TError,
    CreateAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["createAppointment"],
    mutationFn: (variables?: CreateAppointmentMutationVariables) =>
      graphqlFetcher<CreateAppointmentMutation, CreateAppointmentMutationVariables>(
        CreateAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const UpdateAppointmentDocument = `
    mutation updateAppointment($input: UpdateAppointmentInput!) {
  updateAppointment(input: $input) {
    appointment {
      ...Appointment
    }
    workerCollisions {
      id
      name
    }
    locationCollisions {
      id
      name
    }
    success
    message
  }
}
    ${AppointmentFragmentDoc}`;

export const useUpdateAppointmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateAppointmentMutation,
    TError,
    UpdateAppointmentMutationVariables,
    TContext
  >
) => {
  return useMutation<
    UpdateAppointmentMutation,
    TError,
    UpdateAppointmentMutationVariables,
    TContext
  >({
    mutationKey: ["updateAppointment"],
    mutationFn: (variables?: UpdateAppointmentMutationVariables) =>
      graphqlFetcher<UpdateAppointmentMutation, UpdateAppointmentMutationVariables>(
        UpdateAppointmentDocument,
        variables
      )(),
    ...options,
  });
};

export const UpdateClientDocument = `
    mutation updateClient($input: UpdateClientInput!) {
  updateClient(input: $input) {
    client {
      id
      name
      firstName
      lastName
      dateOfBirth
      phone
      email
    }
  }
}
    `;

export const useUpdateClientMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    UpdateClientMutation,
    TError,
    UpdateClientMutationVariables,
    TContext
  >
) => {
  return useMutation<UpdateClientMutation, TError, UpdateClientMutationVariables, TContext>({
    mutationKey: ["updateClient"],
    mutationFn: (variables?: UpdateClientMutationVariables) =>
      graphqlFetcher<UpdateClientMutation, UpdateClientMutationVariables>(
        UpdateClientDocument,
        variables
      )(),
    ...options,
  });
};

export const GetServicesDocument = `
    query getServices {
  services {
    id
    name
    description
    duration
    price
    color
    requiredWorkers
    requiredSpaces
    workers {
      id
      name
      email
    }
    spaces {
      id
      name
    }
  }
}
    `;

export const useGetServicesQuery = <TData = GetServicesQuery, TError = unknown>(
  variables?: GetServicesQueryVariables,
  options?: Omit<UseQueryOptions<GetServicesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<GetServicesQuery, TError, TData>["queryKey"];
  }
) => {
  return useQuery<GetServicesQuery, TError, TData>({
    queryKey: variables === undefined ? ["getServices"] : ["getServices", variables],
    queryFn: graphqlFetcher<GetServicesQuery, GetServicesQueryVariables>(
      GetServicesDocument,
      variables
    ),
    ...options,
  });
};

useGetServicesQuery.getKey = (variables?: GetServicesQueryVariables) =>
  variables === undefined ? ["getServices"] : ["getServices", variables];
